import {
  type DragEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocale } from '../../shared/i18n/LocaleContext'
import { calculatePlanning, createQuickMaintenanceStop, getPlanningSchedule, previewMaintenanceReplan, replanMaintenance, setOperationFixed } from './planningApi'
import { useAppConfirm } from '../../shared/components/AppConfirmDialog'
import { Link, useSearchParams } from 'react-router-dom'
import { getProductionOrder } from '../productionOrders/productionOrdersApi'
import { ProductionOrderDetail } from '../productionOrders/ProductionOrdersPage'
import type { ProductionOrdersSnapshot } from '../productionOrders/types'
import type { PlanningExecutionSegment, PlanningManualAdjustment, PlanningOperation, PlanningSnapshot } from './types'
import type { PlanningResource } from './types'
import { AppModal } from '../../shared/components/AppModal'
import { LocalizedClockInput, LocalizedDateInput } from '../../shared/i18n/LocalizedInputs'
import { useAuth } from '../auth/AuthContext'
import { PagedListControls } from '../../shared/components/PagedListControls'
import { getPreferredPageSize, rememberPageSize, type RecordListPageSize } from '../../shared/pagination/pageSizePreference'

const dayInMilliseconds = 24 * 60 * 60 * 1000
const minuteInMilliseconds = 60 * 1000
type Adjustment = Omit<PlanningManualAdjustment, 'orderId'>
type AdjustmentMap = Record<string, Adjustment>
type DragState = {
  orderId: string
  operationId: string
}
type DragPreview = {
  resourceId: string
  left: number
  requestedStart: number
}
type OperationDetail = {
  operation: PlanningOperation
  anchor: { left: number; top: number; right: number; bottom: number }
  pinned: boolean
}
type PlanningResourceRow = {
  resource: PlanningResource
  operations: PlanningOperation[]
  maintenance: PlanningSnapshot['maintenanceWindows']
  executionSegments: PlanningExecutionSegment[]
  isAlternative: boolean
  isSimultaneous: boolean
  groupStart: boolean
  externalMilestones: PlanningOperation[]
}

function currentPlanningStart(intervalMinutes = 1) {
  const interval = intervalMinutes * minuteInMilliseconds
  return Math.ceil(Date.now() / interval) * interval
}

function currentViewStart(leadHours = 2, intervalMinutes = 15) {
  const interval = intervalMinutes * minuteInMilliseconds
  const roundedCurrentTime = Math.floor(Date.now() / interval) * interval
  return roundedCurrentTime - leadHours * 60 * minuteInMilliseconds
}

type ScenarioChangeSummary = {
  affectedOrders: number
  dateChanges: number
  resourceChanges: number
  scheduledOperations: number
  unscheduledOperations: number
  issuesAdded: number
  issuesResolved: number
}

function planningChangeSummary(current: PlanningSnapshot, candidate: PlanningSnapshot): ScenarioChangeSummary {
  const issueKey = (issue: PlanningSnapshot['issues'][number]) =>
    `${issue.orderId}|${issue.operationId ?? ''}|${issue.code}|${issue.detail}`
  const before = new Map(current.operations.map(operation => [operation.operationId, operation]))
  const after = new Map(candidate.operations.map(operation => [operation.operationId, operation]))
  const affectedOrders = new Set<string>()
  let dateChanges = 0
  let resourceChanges = 0
  let scheduledOperations = 0
  let unscheduledOperations = 0
  for (const [id, operation] of after) {
    const previous = before.get(id)
    if (!previous) { scheduledOperations++; affectedOrders.add(operation.orderId); continue }
    if (previous.startUtc !== operation.startUtc || previous.endUtc !== operation.endUtc) {
      dateChanges++; affectedOrders.add(operation.orderId)
    }
    if (previous.resourceId !== operation.resourceId) {
      resourceChanges++; affectedOrders.add(operation.orderId)
    }
  }
  for (const [id, operation] of before)
    if (!after.has(id)) { unscheduledOperations++; affectedOrders.add(operation.orderId) }
  const beforeIssues = new Set(current.issues.map(issueKey))
  const afterIssues = new Set(candidate.issues.map(issueKey))
  const addedIssues = candidate.issues.filter(issue => !beforeIssues.has(issueKey(issue)))
  const resolvedIssues = current.issues.filter(issue => !afterIssues.has(issueKey(issue)))
  addedIssues.forEach(issue => affectedOrders.add(issue.orderId))
  resolvedIssues.forEach(issue => affectedOrders.add(issue.orderId))
  return { affectedOrders:affectedOrders.size, dateChanges, resourceChanges,
    scheduledOperations, unscheduledOperations, issuesAdded:addedIssues.length,
    issuesResolved:resolvedIssues.length }
}

function hasPlanningChanges(current: PlanningSnapshot, candidate: PlanningSnapshot) {
  const summary = planningChangeSummary(current, candidate)
  if (Object.entries(summary).some(([key, value]) => key !== 'affectedOrders' && value > 0))
    return true
  const windowKey = (window: PlanningSnapshot['maintenanceWindows'][number]) =>
    `${window.workOrderId}|${window.resourceId}|${window.startUtc}|${window.endUtc}`
  const currentWindows = new Set(current.maintenanceWindows.map(windowKey))
  const candidateWindows = new Set(candidate.maintenanceWindows.map(windowKey))
  return currentWindows.size !== candidateWindows.size ||
    [...currentWindows].some(key => !candidateWindows.has(key))
}

export function PlanningPage() {
  const { user } = useAuth()
  const canEditPlanning = user?.permissions.planning === 'Write'
  const { t, locale, formatNumber, formatDateTime } = useLocale()
  const { confirm, confirmationDialog } = useAppConfirm()
  const [searchParams, setSearchParams] = useSearchParams()
  const [viewDays, setViewDays] = useState(3)
  const [viewStart, setViewStart] = useState<number | null>(null)
  const [adjustments, setAdjustments] = useState<AdjustmentMap>({})
  const [data, setData] = useState<PlanningSnapshot | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [dragPreview, setDragPreview] = useState<DragPreview | null>(null)
  const [operationDetail, setOperationDetail] = useState<OperationDetail | null>(null)
  const [orderPanelId, setOrderPanelId] = useState<string | null>(null)
  const [orderData, setOrderData] = useState<ProductionOrdersSnapshot | null>(null)
  const [orderPanelError, setOrderPanelError] = useState<string | null>(null)
  const [quickResource, setQuickResource] = useState<PlanningResource | null>(null)
  const [bottleneckResource, setBottleneckResource] = useState<PlanningResource | null>(null)
  const [maintenanceScenario, setMaintenanceScenario] = useState<{ interruptedOperations: number } | null>(null)
  const [globalScenario, setGlobalScenario] = useState<string | null>(null)
  const [scenarioBaseline, setScenarioBaseline] = useState<PlanningSnapshot | null>(null)
  const [showExternalMilestones, setShowExternalMilestones] = useState(true)
  const [direction, setDirection] = useState<'Forward'|'Backward'|'Hybrid'>('Forward')
  const [respectMaterials, setRespectMaterials] = useState(true)
  const [issueOrderQuery, setIssueOrderQuery] = useState('')
  const [appliedIssueOrderQuery, setAppliedIssueOrderQuery] = useState('')
  const [issuePage, setIssuePage] = useState(1)
  const [issuePageSize, setIssuePageSize] = useState<RecordListPageSize>(() => getPreferredPageSize())
  const [quickStopError, setQuickStopError] = useState<string | null>(null)
  const initialScheduleRequested = useRef(false)
  const dragState = useRef<DragState | null>(null)

  const issueOrderCount = useMemo(() => new Set(data?.issues.map(issue => issue.orderId) ?? []).size, [data?.issues])
  const filteredIssues = useMemo(() => {
    const query = appliedIssueOrderQuery.trim().toLocaleLowerCase(locale)
    return query
      ? data?.issues.filter(issue => issue.orderNumber.toLocaleLowerCase(locale).includes(query)) ?? []
      : data?.issues ?? []
  }, [appliedIssueOrderQuery, data?.issues, locale])
  const issuePageCount = Math.max(1, Math.ceil(filteredIssues.length / issuePageSize))
  const visibleIssues = filteredIssues.slice((issuePage - 1) * issuePageSize, issuePage * issuePageSize)

  useEffect(() => {
    if (issuePage > issuePageCount) setIssuePage(issuePageCount)
  }, [issuePage, issuePageCount])

  useEffect(() => {
    if (!notice) return
    const timeout = window.setTimeout(() => setNotice(null), 4500)
    return () => window.clearTimeout(timeout)
  }, [notice])

  const run = useCallback(async (
    apply = false,
    nextAdjustments: AdjustmentMap = adjustments,
  ) => {
    if (!canEditPlanning) return
    if (apply) {
      const confirmation = data && Object.keys(nextAdjustments).length > 0
        ? t('planning.applyScenarioConfirm')
            .replace('{orders}', formatNumber(data.propagatedOrders))
            .replace('{operations}', formatNumber(data.propagatedOperations))
            .replace('{days}', formatNumber(Math.ceil((new Date(data.horizonEndUtc).getTime() - new Date(data.horizonStartUtc).getTime()) / dayInMilliseconds)))
        : t('planning.applyConfirm')
      if (!await confirm(confirmation)) return
    }
    setBusy(true)
    setError(null)
    const payload: PlanningManualAdjustment[] = Object.entries(nextAdjustments)
      .map(([orderId, adjustment]) => ({ orderId, ...adjustment }))
    try {
      if (!data) return
      const calculationStartMs = currentPlanningStart()
      const calculationStart = new Date(calculationStartMs).toISOString()
      const scenarioBaseEnd = new Date(
        data.originalHorizonEndUtc ?? data.horizonEndUtc,
      ).getTime()
      const latestRequestedStart = payload.length > 0
        ? Math.max(...payload.map((item) =>
            new Date(item.requestedStartUtc).getTime()))
        : calculationStartMs
      const calculationDays = Math.max(30, Math.min(730, Math.ceil(
        (Math.max(scenarioBaseEnd, latestRequestedStart + dayInMilliseconds) -
          calculationStartMs) / dayInMilliseconds,
      )))
      const baseline = scenarioBaseline ?? data
      if (apply) {
        const current = await getPlanningSchedule()
        if (current.publicationId !== baseline.publicationId ||
            hasPlanningChanges(baseline, current))
          throw new Error('planning.scenarioStale')
      }
      const snapshot = await calculatePlanning(calculationStart, calculationDays,
        apply, payload, baseline.publicationId, direction, respectMaterials)
      setData(apply ? await getPlanningSchedule() : snapshot)
      if (apply) { setAdjustments({}); setScenarioBaseline(null) }
      else setScenarioBaseline(baseline)
    } catch (reason) {
      const code = reason instanceof Error ? reason.message : 'planning.loadError'
      if (code === 'planning.scenarioStale') {
        setData(await getPlanningSchedule().catch(() => data))
        setAdjustments({})
        setScenarioBaseline(null)
      }
      setError(code)
    } finally {
      setBusy(false)
    }
  }, [adjustments, canEditPlanning, confirm, data, direction, formatNumber, respectMaterials, scenarioBaseline, t])

  useEffect(() => {
    if (initialScheduleRequested.current) return
    initialScheduleRequested.current = true
    setBusy(true)
    void getPlanningSchedule()
      .then(async (snapshot) => {
        const requestedScenario = canEditPlanning ? searchParams.get('scenario') : null
        const calculationStart = new Date(currentPlanningStart()).toISOString()
        const displayed = requestedScenario === 'maintenance'
          ? await previewMaintenanceReplan()
          : requestedScenario === 'global'
            ? await calculatePlanning(calculationStart, 730, false, [], snapshot.publicationId)
            : snapshot
        setData(displayed)
        if (requestedScenario === 'maintenance') {
          if (hasPlanningChanges(snapshot, displayed)) {
            setScenarioBaseline(snapshot)
            setMaintenanceScenario({
              interruptedOperations: displayed.operations.filter(
                (operation) => operation.isPropagated,
              ).length,
            })
          } else setNotice('planning.noChanges')
        } else if (requestedScenario === 'global') {
          if (hasPlanningChanges(snapshot, displayed)) {
            setScenarioBaseline(snapshot)
            setGlobalScenario(calculationStart)
          } else setNotice('planning.noChanges')
        }
        if (requestedScenario) setSearchParams({}, { replace: true })
        setViewStart(currentViewStart())
      })
      .catch((reason) => setError(reason instanceof Error ? reason.message : 'planning.loadError'))
      .finally(() => setBusy(false))
  }, [canEditPlanning, searchParams, setSearchParams])

  useEffect(() => {
    if (!orderPanelId) return
    const controller = new AbortController()
    setOrderData(null)
    setOrderPanelError(null)
    void getProductionOrder(orderPanelId, controller.signal)
      .then(setOrderData)
      .catch((reason) => {
        if (!controller.signal.aborted) {
          setOrderPanelError(reason instanceof Error ? reason.message : 'orders.loadError')
        }
      })
    return () => controller.abort()
  }, [orderPanelId])

  const hasPendingScenario = Object.keys(adjustments).length > 0 || maintenanceScenario !== null || globalScenario !== null
  const scenarioChanges = useMemo(() => scenarioBaseline && data
    ? planningChangeSummary(scenarioBaseline, data) : null, [data, scenarioBaseline])
  useEffect(() => {
    if (!hasPendingScenario) return
    const protectScenario = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.ctrlKey ||
          event.metaKey || event.shiftKey || event.altKey) return
      const target = event.target
      if (!(target instanceof Element)) return
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null
      if (!anchor || anchor.target === '_blank' || anchor.download ||
          anchor.origin !== window.location.origin ||
          anchor.href === window.location.href) return
      event.preventDefault()
      event.stopImmediatePropagation()
      void confirm(t('planning.leaveScenarioConfirm')).then((accepted) => {
        if (accepted) window.location.assign(anchor.href)
      })
    }
    document.addEventListener('click', protectScenario, true)
    return () => document.removeEventListener('click', protectScenario, true)
  }, [confirm, hasPendingScenario, t])

  const rows = useMemo(() => buildPlanningResourceRows(data, locale), [data, locale])
  const ordersWithOperationWithoutTime = useMemo(() => new Set(
    data?.issues
      .filter((issue) => issue.code === 'planning.operationWithoutTime')
      .map((issue) => issue.orderId) ?? [],
  ), [data])

  const horizonStart = data ? new Date(data.horizonStartUtc).getTime() : 0
  const horizonEnd = data ? new Date(data.horizonEndUtc).getTime() : 1
  const visibleStart = viewStart ?? horizonStart
  const visibleEnd = visibleStart + viewDays * dayInMilliseconds
  const visibleRange = Math.max(1, visibleEnd - visibleStart)
  const tickHours = viewDays === 1 ? 2 : viewDays === 3 ? 6 : viewDays <= 7 ? 24 : viewDays <= 14 ? 48 : viewDays <= 30 ? 72 : 168
  const tickCount = Math.round(viewDays * 24 / tickHours) + 1
  const axisTicks = Array.from(
    { length: tickCount },
    (_, index) => visibleStart + index * tickHours * 60 * minuteInMilliseconds,
  )
  const gridSize = 100 / Math.max(1, tickCount - 1)
  const currentTime = Date.now()
  const showCurrentTime = currentTime >= visibleStart && currentTime <= visibleEnd
  const originalHorizonEnd = data?.originalHorizonEndUtc
    ? new Date(data.originalHorizonEndUtc).getTime()
    : horizonEnd
  const horizonExtended = horizonEnd > originalHorizonEnd

  const position = (value: string) => (
    (new Date(value).getTime() - visibleStart) / visibleRange * 100
  )
  const navigate = (direction: -1 | 1) => {
    setViewStart(visibleStart + direction * viewDays * dayInMilliseconds)
  }
  const goToToday = () => {
    setViewStart(currentViewStart())
  }
  const replan = async () => {
    if (!canEditPlanning) return
    if (hasPendingScenario) {
      setError('planning.scenarioAlreadyOpen')
      return
    }
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const baseline = await getPlanningSchedule()
      const calculationStart = new Date(currentPlanningStart()).toISOString()
      const preview = await calculatePlanning(calculationStart, 730, false, [],
        baseline.publicationId)
      if (!hasPlanningChanges(baseline, preview)) {
        setData(baseline)
        setNotice('planning.noChanges')
        return
      }
      setScenarioBaseline(baseline)
      setData(preview)
      setGlobalScenario(calculationStart)
      setAdjustments({})
      goToToday()
    } catch (reason) {
      const code = reason instanceof Error ? reason.message : 'planning.loadError'
      if (code === 'planning.scenarioStale') {
        setData(await getPlanningSchedule().catch(() => data))
        setGlobalScenario(null)
        setScenarioBaseline(null)
      }
      setError(code)
    } finally {
      setBusy(false)
    }
  }
  const finishGlobalScenario = async (apply: boolean) => {
    setBusy(true); setError(null)
    try {
      if (apply && globalScenario && scenarioBaseline) {
        const current = await getPlanningSchedule()
        if (current.publicationId !== scenarioBaseline.publicationId ||
            hasPlanningChanges(scenarioBaseline, current))
          throw new Error('planning.scenarioStale')
        await calculatePlanning(globalScenario, 730, true, [],
          scenarioBaseline.publicationId)
      }
      setData(await getPlanningSchedule())
      setGlobalScenario(null)
      setScenarioBaseline(null)
    } catch (reason) {
      const code = reason instanceof Error ? reason.message : 'planning.loadError'
      if (code === 'planning.scenarioStale') {
        setData(await getPlanningSchedule().catch(() => data))
        setGlobalScenario(null)
        setScenarioBaseline(null)
      }
      setError(code)
    } finally { setBusy(false) }
  }

  const pointerDate = (clientX: number, track: HTMLElement) => {
    const rectangle = track.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (clientX - rectangle.left) / rectangle.width))
    return visibleStart + ratio * visibleRange
  }
  const beginDrag = (event: DragEvent<HTMLButtonElement>, operation: PlanningOperation) => {
    if (!canEditPlanning || operation.isOrderLocked || busy) {
      event.preventDefault()
      return
    }
    dragState.current = {
      orderId: operation.orderId,
      operationId: operation.operationId,
    }
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', operation.orderId)
  }
  const dragProposal = (clientX: number, track: HTMLElement) => {
    const dragging = dragState.current
    if (!dragging || !data || busy) return
    const orderOperations = data.operations.filter((operation) => operation.orderId === dragging.orderId)
    if (orderOperations.length === 0) return
    // Dropping establishes the operation start. It must not depend on where
    // inside the bar the user happened to grab it.
    const requestedOperationStart = pointerDate(clientX, track)
    const snapMinutes = 15
    const snappedOrderStart = Math.round(
      requestedOperationStart / (snapMinutes * minuteInMilliseconds),
    ) * snapMinutes * minuteInMilliseconds
    const minimumStart = Math.max(horizonStart,
      currentPlanningStart(snapMinutes))
    const boundedOrderStart = Math.max(minimumStart,
      Math.min(horizonEnd - minuteInMilliseconds, snappedOrderStart))
    const left = Math.max(0, Math.min(100,
      (boundedOrderStart - visibleStart) / visibleRange * 100))
    return { dragging, orderOperations, boundedOrderStart, left }
  }
  const previewDrop = (event: DragEvent<HTMLDivElement>, resourceId: string) => {
    if (!canEditPlanning || !dragState.current) return
    const operation = data?.operations.find(item =>
      item.operationId === dragState.current?.operationId)
    if (!operation || resourceId !== operation.resourceId &&
        !operation.alternativeResourceIds.includes(resourceId)) return
    event.preventDefault()
    const proposal = dragProposal(event.clientX, event.currentTarget)
    if (!proposal) return
    setDragPreview({
      resourceId,
      left: proposal.left,
      requestedStart: proposal.boundedOrderStart,
    })
  }
  const dropOrder = (event: DragEvent<HTMLDivElement>, targetResourceId: string) => {
    if (!canEditPlanning) return
    event.preventDefault()
    const proposal = dragProposal(event.clientX, event.currentTarget)
    dragState.current = null
    setDragPreview(null)
    if (!proposal) return
    const anchor = proposal.orderOperations.find(operation =>
      operation.operationId === proposal.dragging.operationId)
    if (!anchor || targetResourceId !== anchor.resourceId &&
        !anchor.alternativeResourceIds.includes(targetResourceId)) return
    const operationResources = proposal.orderOperations
      .filter((operation) => operation.resourceId)
      .map((operation) => ({
        operationId: operation.operationId,
        resourceId: operation.operationId === proposal.dragging.operationId
          ? targetResourceId : operation.resourceId!,
      }))
    const nextAdjustments = {
      ...adjustments,
      [proposal.dragging.orderId]: {
        requestedStartUtc: new Date(proposal.boundedOrderStart).toISOString(),
        anchorOperationId: proposal.dragging.operationId,
        operationResources,
      },
    }
    setAdjustments(nextAdjustments)
    void run(false, nextAdjustments)
  }
  const resetManualAdjustments = () => {
    setAdjustments({})
    setBusy(true); setError(null)
    void getPlanningSchedule().then(setData)
      .catch(reason => setError(reason instanceof Error ? reason.message : 'planning.loadError'))
      .finally(() => { setScenarioBaseline(null); setBusy(false) })
  }
  const openQuickStop = (resource: PlanningResource) => {
    if (hasPendingScenario) {
      setError('planning.applyOrResetBeforeMaintenance')
      return
    }
    setQuickStopError(null)
    setQuickResource(resource)
  }
  const saveQuickStop = async (request: Parameters<typeof createQuickMaintenanceStop>[0]) => {
    setBusy(true); setQuickStopError(null)
    try {
      const result = await createQuickMaintenanceStop(request)
      setQuickResource(null)
      try {
        const preview = await previewMaintenanceReplan()
        setData(preview)
        if (data && hasPlanningChanges(data, preview)) {
          setScenarioBaseline(data)
          setMaintenanceScenario({ interruptedOperations: result.interruptedOperations })
        } else setNotice('planning.noChanges')
      } catch {
        setData(await getPlanningSchedule())
        setError('planning.maintenancePreviewError')
      }
    } catch (reason) {
      setQuickStopError(reason instanceof Error ? reason.message : 'maintenance.actionError')
    } finally { setBusy(false) }
  }
  const finishMaintenanceScenario = async (apply: boolean) => {
    setBusy(true); setError(null)
    try {
      setData(apply ? await replanMaintenance() : await getPlanningSchedule())
      setMaintenanceScenario(null)
      setScenarioBaseline(null)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'planning.loadError')
    } finally { setBusy(false) }
  }
  const showOperationDetail = (
    operation: PlanningOperation,
    element: HTMLElement,
    pinned = false,
  ) => {
    const rectangle = element.getBoundingClientRect()
    setOperationDetail({
      operation,
      pinned,
      anchor: { left: rectangle.left, top: rectangle.top, right: rectangle.right, bottom: rectangle.bottom },
    })
  }
  const closeOperationDetail = () => setOperationDetail(null)
  const openOrderPanel = (orderId: string) => {
    closeOperationDetail()
    setOrderPanelId(orderId)
  }
  const toggleOperationFixed = async (operation: PlanningOperation) => {
    if (!canEditPlanning || busy) return
    if (hasPendingScenario) {
      setError('planning.applyOrResetBeforeFixing')
      return
    }
    setBusy(true)
    setError(null)
    try {
      setData(await setOperationFixed(operation.operationId, !operation.isFixed))
      closeOperationDetail()
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'planning.loadError')
    } finally {
      setBusy(false)
    }
  }
  const selectedOrder = orderData?.orders.find((order) => order.id === orderPanelId)
  const selectedArticle = orderData?.articles.find((article) =>
    article.id === selectedOrder?.articleId)

  return <main className="page-content planning-page">
    <header className="welcome administration-heading">
      <div>
        <span className="eyebrow">{t('planning.eyebrow')}</span>
        <h1>{t('planning.title')}</h1>
        <p>{data?.companyName ?? t('planning.subtitle')}</p>
      </div>
      {canEditPlanning && <button className="secondary-button" type="button" disabled={busy || hasPendingScenario} onClick={() => void replan()}>{t('planning.replanAll')}</button>}
    </header>

    {error && <p className="form-error">{t(error as Parameters<typeof t>[0])}</p>}
    {notice && <p className="form-success" role="status">{t(notice as Parameters<typeof t>[0])}</p>}
    {!data ? (
      <section className="planning-empty" aria-live="polite">
        <strong>{t(busy ? 'planning.loading' : 'planning.ready')}</strong>
        <p>{t(busy ? 'planning.loadingDescription' : 'planning.readyDescription')}</p>
      </section>
    ) : <>
      <section className="planning-kpis">
        <article><span>{t('planning.scheduledOrders')}</span><strong>{formatNumber(data.scheduledOrders)}</strong></article>
        <article><span>{t('planning.scheduledOperations')}</span><strong>{formatNumber(data.scheduledOperations)}</strong></article>
        <article className={data.lateOrders ? 'warning' : ''}><span>{t('planning.lateOrders')}</span><strong>{formatNumber(data.lateOrders)}</strong></article>
        <article className={data.issues.length ? 'warning' : ''}><span>{t('planning.ordersWithIssues')}</span><strong>{formatNumber(issueOrderCount)}</strong><small>{t('planning.totalIssues').replace('{count}', formatNumber(data.issues.length))}</small></article>
      </section>

      {hasPendingScenario && scenarioChanges && <section className="planning-impact data-panel" aria-label={t('planning.scenarioImpact')}>
        <div className="planning-impact-metric orders"><ScenarioMetricIcon kind="orders"/><span>{t('planning.affectedOrders')}</span><strong>{formatNumber(scenarioChanges.affectedOrders)}</strong></div>
        <div className="planning-impact-metric phases"><ScenarioMetricIcon kind="phases"/><span>{t('planning.dateChanges')}</span><strong>{formatNumber(scenarioChanges.dateChanges)}</strong></div>
        <div className="planning-impact-metric calculated"><ScenarioMetricIcon kind="calculated"/><span>{t('planning.resourceChanges')}</span><strong>{formatNumber(scenarioChanges.resourceChanges)}</strong></div>
        <div className="planning-impact-metric original"><ScenarioMetricIcon kind="original"/><span>{t('planning.scheduledChanges')}</span><strong>+{formatNumber(scenarioChanges.scheduledOperations)} / -{formatNumber(scenarioChanges.unscheduledOperations)}</strong></div>
        <div className={`planning-impact-metric ${scenarioChanges.issuesAdded ? 'extended' : ''}`}><ScenarioMetricIcon kind="phases"/><span>{t('planning.issueChanges')}</span><strong>+{formatNumber(scenarioChanges.issuesAdded)} / -{formatNumber(scenarioChanges.issuesResolved)}</strong></div>
        {horizonExtended && <p>{t('planning.extendedHorizon')} · {formatDateTime(data.horizonEndUtc)}</p>}
      </section>}

      <section className="planning-board data-panel">
        <div className="planning-board-heading">
          <div><h2>{t('planning.resourcePlan')}</h2>{canEditPlanning && <p>{t('planning.dragHint')}</p>}</div>
          <div className="planning-legend">
            <span><i className="normal" />{t('planning.onTime')}</span>
            <span><i className="in-progress" />{t('planning.inProgress')}</span>
            <span><i className="completed" />{t('planning.completed')}</span>
            <span><i className="executed-segment" />{t('planning.executedBeforeStop')}</span>
            <span><i className="late" />{t('planning.late')}</span>
            <span><i className="fixed" />{t('planning.fixed')}</span>
            <span><i className="propagated" />{t('planning.propagated')}</span>
            <span><i className="resource-changed" />{t('planning.resourceChanged')}</span>
            <span><i className="maintenance" />{t('planning.maintenanceStop')}</span>
            <span><i className="external-milestone" />{t('planning.externalMilestone')}</span>
            <span><i className="alternative-capacity" />{t('planning.alternativeCapacity')}</span>
            <span><i className="simultaneous-capacity" />{t('planning.simultaneousCapacity')}</span>
          </div>
        </div>
        {Object.keys(adjustments).length > 0 && <div className="planning-pending" role="status">
          <i aria-hidden="true" />
          <div><strong>{t('planning.pendingChanges')}</strong><span>{t('planning.pendingChangesDescription')}</span></div>
          <button className="primary-button" type="button" disabled={busy} onClick={() => void run(true, adjustments)}>{t('planning.apply')}</button>
        </div>}
        {maintenanceScenario && <div className="planning-pending maintenance-scenario" role="status">
          <i aria-hidden="true" />
          <div><strong>{t('planning.maintenanceScenarioReady')}</strong><span>{t('planning.maintenanceScenarioDescription').replace('{operations}', formatNumber(maintenanceScenario.interruptedOperations))}</span></div>
          <button className="secondary-button" type="button" disabled={busy} onClick={() => void finishMaintenanceScenario(false)}>{t('planning.keepCurrentPlan')}</button>
          <button className="primary-button" type="button" disabled={busy} onClick={() => void finishMaintenanceScenario(true)}>{t('planning.applyMaintenanceScenario')}</button>
        </div>}
        {globalScenario && <div className="planning-pending" role="status">
          <i aria-hidden="true" />
          <div><strong>{t('planning.globalScenarioReady')}</strong><span>{t('planning.globalScenarioDescription')}</span></div>
          <button className="secondary-button" type="button" disabled={busy} onClick={() => void finishGlobalScenario(false)}>{t('planning.keepCurrentPlan')}</button>
          <button className="primary-button" type="button" disabled={busy} onClick={() => void finishGlobalScenario(true)}>{t('planning.applyGlobalScenario')}</button>
        </div>}
        <div className="planning-timeline-toolbar">
          {canEditPlanning && <label><span>{t('planning.direction')}</span>
            <select value={direction} onChange={event => setDirection(event.target.value as typeof direction)}>
              <option value="Forward">{t('planning.direction.Forward')}</option>
              <option value="Backward">{t('planning.direction.Backward')}</option>
              <option value="Hybrid">{t('planning.direction.Hybrid')}</option>
            </select></label>}
          {canEditPlanning && <label className="planning-milestone-toggle"><input
            type="checkbox" checked={respectMaterials}
            onChange={event => setRespectMaterials(event.target.checked)} />
            <span>{t('planning.respectMaterials')}</span></label>}
          <div className="planning-period-navigation">
            <button type="button" onClick={() => navigate(-1)} aria-label={t('planning.previousPeriod')} title={t('planning.previousPeriod')}><ChevronIcon direction="left" /></button>
            <button type="button" onClick={goToToday}>{t('planning.today')}</button>
            <button type="button" onClick={() => navigate(1)} aria-label={t('planning.nextPeriod')} title={t('planning.nextPeriod')}><ChevronIcon direction="right" /></button>
          </div>
          <label><span>{t('planning.zoom')}</span><select value={viewDays} onChange={(event) => setViewDays(Number(event.target.value))}>
            {[1, 3, 7, 14, 30, 90].map((value) => <option key={value} value={value}>{t('planning.zoomDays').replace('{days}', formatNumber(value))}</option>)}
          </select></label>
          <label className="planning-milestone-toggle"><input type="checkbox"
            checked={showExternalMilestones}
            onChange={(event) => setShowExternalMilestones(event.target.checked)} />
            <span>{t('planning.showExternalMilestones')}</span>
          </label>
          {Object.keys(adjustments).length > 0 && <button className="secondary-button" type="button" disabled={busy} onClick={resetManualAdjustments}>{t('planning.resetManual')}</button>}
        </div>
        <div className="planning-axis planning-detailed-axis">
          {axisTicks.map((tick) => <span key={tick}>{formatAxisTick(tick, locale, viewDays)}</span>)}
        </div>
        <div className="planning-resource-list">
          {rows.map(({ resource, operations, maintenance, executionSegments,
            isAlternative, isSimultaneous, groupStart, externalMilestones }) => {
            const { laidOut, segmentLanes, laneCount } = assignTimelineLanes(
              operations, executionSegments,
            )
            return <div className={`planning-resource-row ${resource.isExternal ? 'external' : ''} ${(isAlternative || isSimultaneous) && operations.length === 0 ? 'related-capacity' : ''} ${groupStart ? 'capacity-group-start' : ''}`} style={{ minHeight: `${laneCount * 44 + 18}px` }} key={resource.id}
              onContextMenu={user?.permissions.maintenance === 'Write' && canEditPlanning
                ? (event) => { event.preventDefault(); openQuickStop(resource) }
                : undefined}>
            <div className="planning-resource-name">
              <strong>{resource.code}</strong>
              <span>{resource.name}</span>
              <div className="planning-resource-signals">
                {resource.isBottleneck && <small className="structural">{t('planning.structuralCritical')}</small>}
                {resource.isExternal && <small className="external">{t('subcontracting.external')}</small>}
                {isAlternative && <small className="alternative-capacity">{t('planning.alternativeCapacity')}</small>}
                {isSimultaneous && <small className="simultaneous-capacity">{t('planning.simultaneousCapacity')}</small>}
                {resource.bottleneckLevel !== 'Normal' && <button type="button"
                  className={`planning-bottleneck-badge ${resource.bottleneckLevel.toLowerCase()}`}
                  onClick={() => setBottleneckResource(resource)}
                  title={t('planning.bottleneckOpenDetails')}>
                  {t(`planning.bottleneckLevel.${resource.bottleneckLevel}` as Parameters<typeof t>[0])} · {formatNumber(resource.bottleneckScore)}
                </button>}
              </div>
              {user?.permissions.maintenance === 'Write' && canEditPlanning && <button type="button" className="planning-resource-action"
                title={t('planning.quickStop')} aria-label={t('planning.quickStop')}
                onClick={() => openQuickStop(resource)}>•••</button>}
            </div>
            <div
              className="planning-track planning-detailed-track"
              style={{ backgroundSize: `${gridSize}% 100%`, minHeight: `${laneCount * 44 + 18}px` }}
              onDragOver={canEditPlanning && !globalScenario && !maintenanceScenario ? (event) => previewDrop(event, resource.id) : undefined}
              onDrop={canEditPlanning && !globalScenario && !maintenanceScenario ? (event) => dropOrder(event, resource.id) : undefined}
            >
              {maintenance.filter((window) => new Date(window.endUtc).getTime() > visibleStart && new Date(window.startUtc).getTime() < visibleEnd).map((window) => {
                const left = Math.max(0, position(window.startUtc))
                const width = Math.max(.3, Math.min(100, position(window.endUtc)) - left)
                return <div className="planning-maintenance-window" key={window.workOrderId}
                  style={{ left: `${left}%`, width: `${width}%` }}
                  title={`${window.number} · ${window.title}\n${formatDateTime(window.startUtc)} — ${formatDateTime(window.endUtc)}`}>
                  <span>{window.number}</span>
                </div>
              })}
              {segmentLanes.filter(({ segment }) => new Date(segment.endUtc).getTime() > visibleStart && new Date(segment.startUtc).getTime() < visibleEnd).map(({ segment, lane }) => {
                const left = Math.max(0, position(segment.startUtc))
                const width = Math.max(.12, Math.min(100, position(segment.endUtc)) - left)
                const estimate = segment.isTimeEstimate ? ` · ${t('planning.timeEstimate')}` : ''
                return <div className={`planning-execution-segment ${width < 2.5 ? 'compact' : ''}`}
                  style={{ left: `${left}%`, width: `${width}%`, top: `${lane * 44 + 9}px` }} key={segment.id}
                  title={`${segment.orderNumber} · ${segment.operationCode} · ${t('planning.executedBeforeStop')}${estimate}\n${formatDateTime(segment.startUtc)} — ${formatDateTime(segment.endUtc)}`}>
                  <span>{segment.orderNumber} · {segment.operationCode}</span>
                </div>
              })}
              {dragPreview?.resourceId === resource.id && <div
                className="planning-drag-preview"
                style={{ left: `${dragPreview.left}%` }}
              >
                <span>{t('planning.proposedStart')}</span>
                <strong>{formatDateTime(new Date(dragPreview.requestedStart).toISOString())}</strong>
              </div>}
              {showCurrentTime && <div
                className="planning-now-line"
                style={{ left: `${(currentTime - visibleStart) / visibleRange * 100}%` }}
                title={t('planning.currentTime')}
              />}
              {showExternalMilestones && externalMilestones
                .filter(operation => new Date(operation.endUtc).getTime() >= visibleStart &&
                  new Date(operation.endUtc).getTime() <= visibleEnd)
                .map(operation => {
                  const left = Math.max(0, Math.min(100, position(operation.endUtc)))
                  const label = t('planning.externalMilestoneLabel')
                    .replace('{number}', operation.externalOrderNumber || operation.operationCode)
                  const title = `${label}\n${t('subcontracting.plannedReceipt')}: ${formatDateTime(operation.endUtc)}`
                  const content = <><i aria-hidden="true" /><span>{label}</span></>
                  return operation.externalOrderId &&
                      user?.permissions.subcontracting &&
                      user.permissions.subcontracting !== 'None'
                    ? <Link className="planning-external-milestone" style={{ left: `${left}%` }}
                        to={`/app/subcontratacion/ordenes?edit=${operation.externalOrderId}`}
                        title={title} aria-label={title} key={operation.operationId}>{content}</Link>
                    : <span className="planning-external-milestone" style={{ left: `${left}%` }}
                        title={title} aria-label={title} key={operation.operationId}>{content}</span>
                })}
              {laidOut
                .filter(({ operation }) => new Date(operation.endUtc).getTime() > visibleStart && new Date(operation.startUtc).getTime() < visibleEnd)
                .map(({ operation, lane }) => {
                  const rawLeft = position(operation.startUtc)
                  const rawRight = position(operation.endUtc)
                  const left = Math.max(0, rawLeft)
                  const width = Math.max(.12, Math.min(100, rawRight) - left)
                  const description = `${operation.orderNumber} · ${operation.operationCode}\n${formatDateTime(operation.startUtc)} — ${formatDateTime(operation.endUtc)}`
                  const operationIssues = data.issues.filter((issue) => issue.operationId === operation.operationId || (!issue.operationId && issue.orderId === operation.orderId))
                  const orderHasOperationWithoutTime = ordersWithOperationWithoutTime.has(operation.orderId)
                  const hasIssue = operationIssues.length > 0 || orderHasOperationWithoutTime
                  const selectedOrder = operationDetail?.operation.orderId === operation.orderId
                  const selectedOperation = operationDetail?.operation.operationId === operation.operationId
                  return <button
                    type="button"
                    draggable={canEditPlanning && !resource.isExternal && !globalScenario && !maintenanceScenario && !operation.isOrderLocked && !operation.isCompleted && !operation.isInProgress && !busy}
                    className={`planning-bar ${resource.isExternal ? 'external' : ''} ${width < 2.5 ? 'compact' : ''} ${operation.isCompleted ? 'completed' : operation.isInProgress ? 'in-progress' : operation.isPropagated ? 'propagated' : operation.isResourceChanged ? 'resource-changed' : operation.isFixed || operation.isManuallyAdjusted ? 'fixed' : operation.isLate ? 'late' : ''} ${hasIssue ? 'has-issue' : ''} ${selectedOrder ? 'related-order' : ''} ${selectedOperation ? 'selected-operation' : ''}`}
                    style={{ left: `${left}%`, width: `${width}%`, top: `${lane * 44 + 9}px` }}
                    key={operation.operationId}
                    aria-label={description}
                    onDragStart={canEditPlanning ? (event) => beginDrag(event, operation) : undefined}
                    onClick={(event) => showOperationDetail(operation, event.currentTarget, true)}
                    onContextMenu={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      showOperationDetail(operation, event.currentTarget, true)
                    }}
                    onDoubleClick={() => openOrderPanel(operation.orderId)}
                    onDragEnd={() => {
                      dragState.current = null
                      setDragPreview(null)
                    }}
                  ><span>{operation.orderNumber} · {operation.operationCode}</span>{hasIssue && <i className="planning-issue-marker" aria-hidden="true">!</i>}</button>
                })}
            </div>
          </div>})}
        </div>
      </section>

      {operationDetail && <OperationDetailPopover
        detail={operationDetail}
        issues={data.issues.filter((issue) => issue.operationId === operationDetail.operation.operationId || (!issue.operationId && issue.orderId === operationDetail.operation.orderId))}
        orderHasOperationWithoutTime={ordersWithOperationWithoutTime.has(operationDetail.operation.orderId)}
        formatDateTime={formatDateTime}
        t={t}
        canEdit={canEditPlanning}
        busy={busy}
        onToggleFixed={() => void toggleOperationFixed(operationDetail.operation)}
        onClose={closeOperationDetail}
        onOpenOrder={() => openOrderPanel(operationDetail.operation.orderId)}
      />}
      {bottleneckResource && <AppModal title={t('planning.bottleneckDetails')}
        closeLabel={t('nav.closeMenu')} onClose={() => setBottleneckResource(null)}>
        <div className="bottleneck-detail">
          <header><strong>{bottleneckResource.code} · {bottleneckResource.name}</strong>
            <span className={`planning-bottleneck-badge ${bottleneckResource.bottleneckLevel.toLowerCase()}`}>
              {t(`planning.bottleneckLevel.${bottleneckResource.bottleneckLevel}` as Parameters<typeof t>[0])} · {formatNumber(bottleneckResource.bottleneckScore)}/100
            </span></header>
          <dl>
            <div><dt>{t('planning.bottleneckUtilization')}</dt><dd>{formatNumber(bottleneckResource.utilizationPercent)} %</dd></div>
            <div><dt>{t('planning.bottleneckQueue')}</dt><dd>{formatNumber(bottleneckResource.queueHours)} h</dd></div>
            <div><dt>{t('planning.bottleneckAtRisk')}</dt><dd>{formatNumber(bottleneckResource.atRiskOperations)}</dd></div>
            <div><dt>{t('planning.structuralCritical')}</dt><dd>{t(bottleneckResource.isBottleneck ? 'planning.yes' : 'planning.no')}</dd></div>
          </dl>
          <h3>{t('planning.bottleneckReasons')}</h3>
          {bottleneckResource.bottleneckReasons.length > 0
            ? <ul>{bottleneckResource.bottleneckReasons.map(reason => <li key={reason}>{t(reason as Parameters<typeof t>[0])}</li>)}</ul>
            : <p>{t('planning.bottleneckNoReasons')}</p>}
          <p className="form-hint">{t('planning.bottleneckWindow')}</p>
        </div>
      </AppModal>}
      {quickResource && <AppModal title={t('planning.quickStop')} closeLabel={t('nav.closeMenu')}
        onClose={() => setQuickResource(null)} saveForm saveDisabled={busy}
        feedback={quickStopError ? t(quickStopError as Parameters<typeof t>[0]) : null}>
        <QuickStopForm resource={quickResource} busy={busy} save={saveQuickStop} />
      </AppModal>}

      {data.issues.length > 0 && <section className="data-panel planning-issues">
        <div className="planning-issues-heading"><h2>{t('planning.issues')}</h2></div>
        <PagedListControls applyLabel={t('recordList.applyFilters')} clearLabel={t('recordList.clearFilters')}
          rowsPerPageLabel={t('recordList.rowsPerPage')} previousLabel={t('recordList.previousPage')}
          nextLabel={t('recordList.nextPage')} pageLabel={t('recordList.pageOf')} totalLabel={t('recordList.totalRecords')}
          page={issuePage} pageSize={issuePageSize} totalCount={filteredIssues.length} formatNumber={formatNumber}
          filters={<label><span>{t('orders.number')}</span><input value={issueOrderQuery}
            placeholder={t('planning.issueOrderFilter')} onChange={event => setIssueOrderQuery(event.target.value)} /></label>}
          onApply={() => { setAppliedIssueOrderQuery(issueOrderQuery); setIssuePage(1) }}
          onClear={() => { setIssueOrderQuery(''); setAppliedIssueOrderQuery(''); setIssuePage(1) }}
          onPageChange={setIssuePage} onPageSizeChange={size => { rememberPageSize(size); setIssuePageSize(size); setIssuePage(1) }} />
        <div className="administration-table-wrap">
          <table className="data-table administration-table">
            <thead><tr><th>{t('orders.number')}</th><th>{t('planning.issue')}</th><th>{t('planning.detail')}</th></tr></thead>
            <tbody>{visibleIssues.map((issue, index) => <tr key={`${issue.orderId}-${issue.operationId}-${index}`}>
              <td><strong>{issue.orderNumber}</strong></td>
              <td>{t(issue.code as Parameters<typeof t>[0])}</td>
              <td>{issue.detail || '—'}</td>
            </tr>)}</tbody>
          </table>
        </div>
        {visibleIssues.length === 0 && <p className="empty-state">{t('administration.empty')}</p>}
      </section>}
    </>}
    {orderPanelId && <aside className="planning-order-drawer" role="dialog"
      aria-label={t('orders.viewOrder')}>
      <header>
        <div><span>{t('orders.viewOrder')}</span><strong>{selectedOrder?.number ?? '—'}</strong>{selectedArticle && <small>{selectedArticle.code} · {selectedArticle.name}</small>}</div>
        <button type="button" className="modal-close" aria-label={t('orders.closeOrderPanel')}
          onClick={() => setOrderPanelId(null)}>×</button>
      </header>
      <div className="planning-order-drawer-actions">
        <Link className="secondary-button" target="_blank" rel="noreferrer"
          to={`/app/ordenes-fabricacion/${orderPanelId}`}>{t('orders.openFullRecord')}</Link>
      </div>
      <div className="planning-order-drawer-body">
        {orderPanelError && <p className="form-error">{t(orderPanelError as Parameters<typeof t>[0])}</p>}
        {!orderData && !orderPanelError && <p>{t('orders.loading')}</p>}
        {orderData && !selectedOrder && <p className="form-error">{t('orders.notFound')}</p>}
        {orderData && selectedOrder && <ProductionOrderDetail order={selectedOrder}
          data={orderData} canEdit={false} command={() => undefined} />}
      </div>
    </aside>}
    {confirmationDialog}
  </main>
}

function assignTimelineLanes(
  operations: PlanningOperation[],
  executionSegments: PlanningExecutionSegment[],
) {
  const timeline = [
    ...operations.map((operation) => ({
      kind: 'operation' as const,
      item: operation,
      start: new Date(operation.startUtc).getTime(),
      end: new Date(operation.endUtc).getTime(),
    })),
    ...executionSegments.map((segment) => ({
      kind: 'segment' as const,
      item: segment,
      start: new Date(segment.startUtc).getTime(),
      end: new Date(segment.endUtc).getTime(),
    })),
  ].sort((a, b) => a.start - b.start || a.end - b.end)
  const laneEnds: number[] = []
  const positioned = timeline.map((entry) => {
      let lane = laneEnds.findIndex((end) => end <= entry.start)
      if (lane < 0) lane = laneEnds.length
      laneEnds[lane] = entry.end
      return { ...entry, lane }
    })
  return {
    laidOut: positioned
      .filter((entry) => entry.kind === 'operation')
      .map((entry) => ({ operation: entry.item as PlanningOperation, lane: entry.lane })),
    segmentLanes: positioned
      .filter((entry) => entry.kind === 'segment')
      .map((entry) => ({ segment: entry.item as PlanningExecutionSegment, lane: entry.lane })),
    laneCount: Math.max(1, laneEnds.length),
  }
}

function QuickStopForm({ resource, busy, save }: {
  resource: PlanningResource
  busy: boolean
  save: (request: Parameters<typeof createQuickMaintenanceStop>[0]) => Promise<void>
}) {
  const { t, locale } = useLocale()
  const rounded = new Date(Math.ceil(Date.now() / (15 * minuteInMilliseconds)) * 15 * minuteInMilliseconds)
  const finish = new Date(rounded.getTime() + 2 * 60 * minuteInMilliseconds)
  const localDate = (date: Date) => {
    const offset = date.getTimezoneOffset() * minuteInMilliseconds
    return new Date(date.getTime() - offset).toISOString().slice(0, 10)
  }
  const localTime = (date: Date) => date.toTimeString().slice(0, 5)
  const [form, setForm] = useState({
    title: t('planning.quickStopDefaultTitle'), description: '',
    type: 'Corrective' as 'Corrective' | 'Preventive' | 'Predictive' | 'Inspection',
    priority: 'Urgent' as 'Low' | 'Normal' | 'High' | 'Urgent',
    startDate: localDate(rounded), startTime: localTime(rounded),
    endDate: localDate(finish), endTime: localTime(finish),
  })
  const toUtc = (date: string, time: string) => new Date(`${date}T${time}:00`).toISOString()
  return <form className="administration-form modal-form" onSubmit={(event) => {
    event.preventDefault()
    void save({ resourceId: resource.id, title: form.title,
      description: form.description, type: form.type, priority: form.priority,
      startAtUtc: toUtc(form.startDate, form.startTime),
      endAtUtc: toUtc(form.endDate, form.endTime) })
  }}>
    <p className="quick-stop-resource"><strong>{resource.code}</strong> · {resource.name}</p>
    <div className="compact-form-grid">
      <label><span>{t('aplPanel.name')}</span><input required value={form.title}
        onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
      <label><span>{t('maintenance.type')}</span><select value={form.type}
        onChange={(event) => setForm({ ...form, type: event.target.value as typeof form.type })}>
        {['Corrective', 'Preventive', 'Predictive', 'Inspection'].map((type) =>
          <option value={type} key={type}>{t(`maintenance.type.${type}` as Parameters<typeof t>[0])}</option>)}
      </select></label>
      <label><span>{t('maintenance.priority')}</span><select value={form.priority}
        onChange={(event) => setForm({ ...form, priority: event.target.value as typeof form.priority })}>
        {['Low', 'Normal', 'High', 'Urgent'].map((priority) =>
          <option value={priority} key={priority}>{t(`maintenance.priority.${priority}` as Parameters<typeof t>[0])}</option>)}
      </select></label>
      <label><span>{t('maintenance.plannedStartAtUtc')}</span><div className="maintenance-datetime">
        <LocalizedDateInput locale={locale} required value={form.startDate}
          onChange={(startDate) => setForm({ ...form, startDate })} />
        <LocalizedClockInput locale={locale} required value={form.startTime}
          ariaLabel={t('maintenance.time')} pickerLabel={t('input.selectTime')}
          hourLabel={t('input.hour')} minuteLabel={t('input.minute')}
          dayPeriodLabel={t('input.dayPeriod')}
          onChange={(startTime) => setForm({ ...form, startTime })} />
      </div></label>
      <label><span>{t('maintenance.plannedEndAtUtc')}</span><div className="maintenance-datetime">
        <LocalizedDateInput locale={locale} required value={form.endDate}
          onChange={(endDate) => setForm({ ...form, endDate })} />
        <LocalizedClockInput locale={locale} required value={form.endTime}
          ariaLabel={t('maintenance.time')} pickerLabel={t('input.selectTime')}
          hourLabel={t('input.hour')} minuteLabel={t('input.minute')}
          dayPeriodLabel={t('input.dayPeriod')}
          onChange={(endTime) => setForm({ ...form, endTime })} />
      </div></label>
      <label><span>{t('materials.description')}</span><textarea value={form.description}
        onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
    </div>
    <div className="form-actions"><button className="primary-button" disabled={busy}>
      {t('administration.save')}</button></div>
  </form>
}

function OperationDetailPopover({ detail, issues, orderHasOperationWithoutTime, formatDateTime, t, canEdit,
  busy, onToggleFixed, onClose, onOpenOrder }: {
  detail: OperationDetail
  issues: PlanningSnapshot['issues']
  orderHasOperationWithoutTime: boolean
  formatDateTime: (value: string | Date) => string
  t: ReturnType<typeof useLocale>['t']
  canEdit: boolean
  busy: boolean
  onToggleFixed: () => void
  onClose: () => void
  onOpenOrder: () => void
}) {
  const { operation } = detail
  const popoverRef = useRef<HTMLElement>(null)
  const [position, setPosition] = useState({ left: 12, top: 12, maxHeight: window.innerHeight - 24 })
  useLayoutEffect(() => {
    const placePopover = () => {
      const popover = popoverRef.current
      if (!popover) return
      const margin = 12
      const gap = 12
      const width = popover.offsetWidth
      const height = popover.offsetHeight
      const { anchor } = detail
      const viewportMaxHeight = window.innerHeight - margin * 2
      const clampLeft = (left: number) => Math.max(margin, Math.min(window.innerWidth - width - margin, left))
      const clampTop = (top: number) => Math.max(margin, Math.min(window.innerHeight - height - margin, top))
      if (window.innerWidth - anchor.right - margin >= width + gap) {
        setPosition({ left: anchor.right + gap, top: clampTop(anchor.top + (anchor.bottom - anchor.top - height) / 2), maxHeight: viewportMaxHeight })
      } else if (anchor.left - margin >= width + gap) {
        setPosition({ left: anchor.left - width - gap, top: clampTop(anchor.top + (anchor.bottom - anchor.top - height) / 2), maxHeight: viewportMaxHeight })
      } else if (window.innerHeight - anchor.bottom - margin >= height + gap) {
        setPosition({ left: clampLeft((anchor.left + anchor.right - width) / 2), top: anchor.bottom + gap, maxHeight: window.innerHeight - anchor.bottom - gap - margin })
      } else {
        const spaceAbove = anchor.top - gap - margin
        const spaceBelow = window.innerHeight - anchor.bottom - gap - margin
        setPosition(spaceAbove >= spaceBelow
          ? { left: clampLeft((anchor.left + anchor.right - width) / 2), top: margin, maxHeight: Math.max(40, spaceAbove) }
          : { left: clampLeft((anchor.left + anchor.right - width) / 2), top: anchor.bottom + gap, maxHeight: Math.max(40, spaceBelow) })
      }
    }
    placePopover()
    window.addEventListener('resize', placePopover)
    return () => window.removeEventListener('resize', placePopover)
  }, [detail])
  const elapsedDurationMinutes = Math.max(0, Math.round((new Date(operation.endUtc).getTime() - new Date(operation.startUtc).getTime()) / minuteInMilliseconds))
  const status = operation.isCompleted ? 'planning.completed' : operation.isInProgress ? 'planning.inProgress' : operation.isPropagated ? 'planning.propagated' : operation.isResourceChanged ? 'planning.resourceChanged' : operation.isFixed || operation.isManuallyAdjusted ? 'planning.fixed' : operation.isLate ? 'planning.late' : 'planning.onTime'
  return <aside ref={popoverRef} className="planning-operation-popover" style={{ left: position.left, top: position.top, maxHeight: position.maxHeight }} role="dialog" aria-label={t('planning.operationDetails')}>
    <div className="planning-operation-popover-heading">
      <div><span>{t('planning.operationDetails')}</span><strong>{operation.orderNumber} · {operation.operationCode}</strong></div>
      {detail.pinned && <button type="button" onClick={onClose}
        aria-label={t('administration.close')}>✕</button>}
    </div>
    <p>{operation.operationName}</p>
    <dl>
      <div><dt>{t('planning.resource')}</dt><dd>{operation.resourceCode}</dd></div>
      <div><dt>{t('planning.status')}</dt><dd><i className={status.replace('planning.', '')} />{t(status)}</dd></div>
      <div><dt>{t('planning.start')}</dt><dd>{formatDateTime(operation.startUtc)}</dd></div>
      <div><dt>{t('planning.end')}</dt><dd>{formatDateTime(operation.endUtc)}</dd></div>
      <div><dt>{t('planning.effectiveDuration')}</dt><dd>{formatDuration(operation.effectiveDurationMinutes)}</dd></div>
      {operation.batchCount > 1 && <div><dt>{t('planning.parallelBatches')}</dt>
        <dd>{operation.batchCount}</dd></div>}
      <div><dt>{t('planning.elapsedDuration')}</dt><dd>{formatDuration(elapsedDurationMinutes)}</dd></div>
      <div><dt>{t('planning.requiredDate')}</dt><dd>{formatDateTime(operation.requiredAtUtc)}</dd></div>
    </dl>
    <div className={`planning-popover-issues ${issues.length || orderHasOperationWithoutTime ? 'warning' : ''}`}>
      {issues.map((issue, index) => <p key={`${issue.code}-${index}`}><strong>{t(issue.code as Parameters<typeof t>[0])}</strong>{issue.detail && <span>{issue.detail}</span>}</p>)}
      {orderHasOperationWithoutTime && <p><strong>{t('planning.orderHasSkippedOperations')}</strong></p>}
      {!issues.length && !orderHasOperationWithoutTime && <p>{t('planning.noRelatedIssues')}</p>}
    </div>
    <div className="planning-operation-actions">
      <button className="secondary-button" type="button"
        onClick={onOpenOrder}>{t('orders.viewOrder')}</button>
      {canEdit && !operation.isCompleted && !operation.isInProgress &&
        <button className="secondary-button" type="button"
          disabled={busy} onClick={onToggleFixed}>
          {t(operation.isFixed ? 'planning.unfixOperation' : 'planning.fixOperation')}
        </button>}
    </div>
  </aside>
}

function formatDuration(totalMinutes: number) {
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return hours ? `${hours} h ${minutes ? `${minutes} min` : ''}`.trim() : `${minutes} min`
}

function buildPlanningResourceRows(data: PlanningSnapshot | null,
  locale: string): PlanningResourceRow[] {
  if (!data) return []
  const byId = new Map(data.resources.map(resource => [resource.id, resource]))
  const internalOperations = data.operations.filter(operation => !operation.isExternal)
  const externalOperations = data.operations.filter(operation => operation.isExternal)
  const planned = new Set(internalOperations.flatMap(operation =>
    operation.resourceId ? [operation.resourceId] : []))
  const alternative = new Set(internalOperations.flatMap(operation =>
    operation.alternativeResourceIds ?? []))
  const simultaneous = new Set(internalOperations.flatMap(operation =>
    operation.simultaneousResourceIds ?? []))
  const visible = new Set([...planned, ...alternative, ...simultaneous]
    .filter(id => byId.has(id) && !byId.get(id)!.isExternal))
  const edges = new Map<string, Set<string>>()
  const connect = (left: string, right: string) => {
    if (left === right || !visible.has(left) || !visible.has(right)) return
    if (!edges.has(left)) edges.set(left, new Set())
    if (!edges.has(right)) edges.set(right, new Set())
    edges.get(left)!.add(right); edges.get(right)!.add(left)
  }
  for (const operation of internalOperations) if (operation.resourceId)
    for (const related of [...(operation.alternativeResourceIds ?? []),
      ...(operation.simultaneousResourceIds ?? [])])
      connect(operation.resourceId, related)
  const collator = new Intl.Collator(locale, { numeric: true, sensitivity: 'base' })
  const compareResource = (left: string, right: string) => {
    const a = byId.get(left)!, b = byId.get(right)!
    return collator.compare(a.code, b.code) || collator.compare(a.name, b.name)
  }
  const seen = new Set<string>()
  const groups = [...visible].sort(compareResource).map(seed => {
    if (seen.has(seed)) return null
    const pending = [seed], members: string[] = []; seen.add(seed)
    while (pending.length) {
      const current = pending.shift()!; members.push(current)
      for (const next of edges.get(current) ?? []) if (!seen.has(next)) {
        seen.add(next); pending.push(next)
      }
    }
    members.sort((left, right) => Number(!planned.has(left)) -
      Number(!planned.has(right)) || compareResource(left, right))
    const primary = members.find(id => planned.has(id)) ?? members[0]
    return { primary, members }
  }).filter((group): group is { primary: string; members: string[] } => group !== null)
    .sort((left, right) => compareResource(left.primary, right.primary))
  return groups.flatMap(group => group.members.map((id, index) => {
    const resource = byId.get(id)!
    const externalMilestones = externalOperations.filter(external => {
      const sameOrder = internalOperations
        .filter(operation => operation.orderId === external.orderId && operation.resourceId)
        .sort((left, right) => left.sequence - right.sequence)
      const anchor = sameOrder.find(operation => operation.sequence > external.sequence) ??
        [...sameOrder].reverse().find(operation => operation.sequence < external.sequence)
      return anchor?.resourceId === id
    })
    return {
      resource,
      operations: internalOperations.filter(operation => operation.resourceId === id),
      maintenance: (data.maintenanceWindows ?? []).filter(window => window.resourceId === id),
      executionSegments: (data.executionSegments ?? []).filter(segment => segment.resourceId === id),
      isAlternative: alternative.has(id),
      isSimultaneous: simultaneous.has(id),
      groupStart: index === 0,
      externalMilestones,
    }
  }))
}

function formatAxisTick(value: number, locale: string, viewDays: number) {
  return new Intl.DateTimeFormat(locale, viewDays <= 3
    ? { weekday: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' }
    : { weekday: 'short', day: '2-digit', month: '2-digit' }).format(new Date(value))
}

function ScenarioMetricIcon({ kind }: {
  kind: 'original' | 'calculated' | 'orders' | 'phases'
}) {
  const paths = {
    original: <><circle cx="10" cy="10" r="6.5"/><path d="M10 6.5v4l2.7 1.7"/></>,
    calculated: <><path d="M3.5 6.5h13v10h-13zM6 3.5v5M14 3.5v5M3.5 9.5h13"/></>,
    orders: <><path d="M4 5.5h12v11H4zM7 3.5h6v4H7zM7 11h6M7 14h4"/></>,
    phases: <><circle cx="5" cy="10" r="2"/><circle cx="15" cy="5" r="2"/><circle cx="15" cy="15" r="2"/><path d="M7 9.3 13 5.7M7 10.7l6 3.6"/></>,
  }
  return <i className="planning-impact-icon" aria-hidden="true"><svg viewBox="0 0 20 20">{paths[kind]}</svg></i>
}

function ChevronIcon({ direction }: { direction: 'left' | 'right' }) {
  return <svg aria-hidden="true" viewBox="0 0 20 20">
    <path d={direction === 'left' ? 'M12.5 4.5 7 10l5.5 5.5' : 'M7.5 4.5 13 10l-5.5 5.5'} />
  </svg>
}
